import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { ChevronDown, HelpCircle, Phone, Mail, MessageCircle } from 'lucide-react';
import { brandTheme } from '../styles/BrandTheme';
import HeroSectionComponent from '../components/HeroSection';

const FAQContainer = styled.div`
  padding-top: 120px;
  min-height: 100vh;
  background: linear-gradient(135deg, ${brandTheme.colors.primary[50]} 0%, ${brandTheme.colors.neutral.white} 100%);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${brandTheme.spacing[20]} ${brandTheme.spacing[6]};

  @media (max-width: 768px) {
    padding: ${brandTheme.spacing[16]} ${brandTheme.spacing[4]};
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${brandTheme.spacing[16]};
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: ${brandTheme.typography.fontWeight.extrabold};
  color: ${brandTheme.colors.secondary[900]};
  margin-bottom: ${brandTheme.spacing[4]};
  background: linear-gradient(135deg, ${brandTheme.colors.primary[600]} 0%, ${brandTheme.colors.secondary[800]} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: ${brandTheme.colors.secondary[600]};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const FAQGrid = styled.div`
  display: grid;
  gap: ${brandTheme.spacing[6]};
  margin-bottom: ${brandTheme.spacing[16]};
`;

const FAQCategory = styled.div`
  background: ${brandTheme.colors.neutral.white};
  border-radius: ${brandTheme.borderRadius['2xl']};
  box-shadow: ${brandTheme.boxShadow.lg};
  border: 1px solid ${brandTheme.colors.neutral.gray[200]};
  overflow: hidden;
`;

const CategoryHeader = styled.div`
  background: linear-gradient(135deg, ${brandTheme.colors.primary[500]} 0%, ${brandTheme.colors.primary[600]} 100%);
  padding: ${brandTheme.spacing[6]};
  color: ${brandTheme.colors.neutral.black};
  
  h2 {
    font-size: 1.5rem;
    font-weight: ${brandTheme.typography.fontWeight.bold};
    margin: 0;
    display: flex;
    align-items: center;
    gap: ${brandTheme.spacing[3]};
  }
`;

const FAQItem = styled.div`
  border-bottom: 1px solid ${brandTheme.colors.neutral.gray[200]};
  
  &:last-child {
    border-bottom: none;
  }
`;

const FAQQuestion = styled.button<{ isOpen: boolean }>`
  width: 100%;
  padding: ${brandTheme.spacing[6]};
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  background: ${props => props.isOpen ? brandTheme.colors.primary[50] : 'transparent'};

  &:hover {
    background: ${brandTheme.colors.primary[50]};
  }

  h3 {
    font-size: 1.125rem;
    font-weight: ${brandTheme.typography.fontWeight.semibold};
    color: ${brandTheme.colors.secondary[900]};
    margin: 0;
    flex: 1;
    text-align: left;
  }

  svg {
    width: 20px;
    height: 20px;
    color: ${brandTheme.colors.primary[600]};
    transition: transform ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const FAQAnswer = styled.div<{ isOpen: boolean }>`
  max-height: ${props => props.isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: max-height ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  background: ${props => props.isOpen ? brandTheme.colors.primary[50] : 'transparent'};
`;

const FAQAnswerContent = styled.div`
  padding: 0 ${brandTheme.spacing[6]} ${brandTheme.spacing[6]};

  p {
    color: ${brandTheme.colors.secondary[700]};
    line-height: 1.6;
    margin: 0 0 ${brandTheme.spacing[4]} 0;
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  ul {
    margin: ${brandTheme.spacing[4]} 0;
    padding-left: ${brandTheme.spacing[6]};
    
    li {
      color: ${brandTheme.colors.secondary[700]};
      margin-bottom: ${brandTheme.spacing[2]};
      line-height: 1.6;
    }
  }
`;

const ContactSection = styled.div`
  background: linear-gradient(135deg, ${brandTheme.colors.secondary[900]} 0%, ${brandTheme.colors.secondary[800]} 100%);
  border-radius: ${brandTheme.borderRadius['2xl']};
  padding: ${brandTheme.spacing[12]};
  text-align: center;
  color: ${brandTheme.colors.neutral.white};

  h2 {
    font-size: 2rem;
    font-weight: ${brandTheme.typography.fontWeight.bold};
    margin-bottom: ${brandTheme.spacing[4]};
    color: ${brandTheme.colors.neutral.white};
  }

  p {
    font-size: 1.125rem;
    margin-bottom: ${brandTheme.spacing[8]};
    color: rgba(255, 255, 255, 0.9);
  }
`;

const ContactButtons = styled.div`
  display: flex;
  gap: ${brandTheme.spacing[4]};
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ContactButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${brandTheme.spacing[2]};
  padding: ${brandTheme.spacing[4]} ${brandTheme.spacing[8]};
  border-radius: ${brandTheme.borderRadius.full};
  text-decoration: none;
  font-weight: ${brandTheme.typography.fontWeight.semibold};
  transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }
`;

const WhatsAppButton = styled(ContactButton)`
  background: linear-gradient(135deg, #25d366 0%, #20c55a 100%);
  color: ${brandTheme.colors.neutral.white};
  box-shadow: 0 4px 15px rgba(37, 211, 102, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(37, 211, 102, 0.4);
  }
`;

const PhoneButton = styled(ContactButton)`
  background: linear-gradient(135deg, ${brandTheme.colors.primary[500]} 0%, ${brandTheme.colors.primary[600]} 100%);
  color: ${brandTheme.colors.neutral.black};
  box-shadow: 0 4px 15px rgba(254, 206, 27, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(254, 206, 27, 0.4);
  }
`;

const EmailButton = styled(ContactButton)`
  background: rgba(255, 255, 255, 0.1);
  color: ${brandTheme.colors.neutral.white};
  border: 2px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

interface FAQItemData {
  question: string;
  answer: string;
}

const faqData = {
  general: [
    {
      question: "What is resin bound surfacing?",
      answer: "Resin bound surfacing is a decorative, permeable surface made by mixing natural aggregates with a clear resin binder. It creates a smooth, durable finish that's perfect for driveways, pathways, and pool areas."
    },
    {
      question: "How long does resin bound surfacing last?",
      answer: "With proper installation and maintenance, resin bound surfacing can last 15-25 years. The lifespan depends on factors like usage, weather conditions, and maintenance routine."
    },
    {
      question: "Is resin bound surfacing permeable?",
      answer: "Yes, resin bound surfacing is fully permeable (SUDS compliant), allowing water to drain through naturally. This helps prevent flooding and is environmentally friendly."
    },
    {
      question: "What areas of London do you cover?",
      answer: "We provide resin bound surfacing services across all London boroughs including Central London, North London, South London, East London, and West London. Contact us to confirm coverage in your specific area."
    }
  ],
  installation: [
    {
      question: "How long does installation take?",
      answer: "Installation time varies depending on the size and complexity of the project. A typical driveway takes 1-3 days, while larger commercial projects may take a week or more."
    },
    {
      question: "Do you provide free quotes?",
      answer: "Yes, we offer completely free, no-obligation quotes. Our team will visit your property to assess the area and provide a detailed estimate."
    },
    {
      question: "What preparation is needed before installation?",
      answer: "We handle all preparation work including excavation, base preparation, and drainage considerations. We'll discuss any specific requirements during your free consultation."
    },
    {
      question: "Can resin bound surfacing be installed over existing surfaces?",
      answer: "In most cases, yes. We can often install over existing concrete, asphalt, or other surfaces after proper preparation. We'll assess this during your free quote."
    }
  ],
  maintenance: [
    {
      question: "How do I maintain resin bound surfacing?",
      answer: "Maintenance is minimal. Regular sweeping and occasional power washing with a mild detergent is usually sufficient. Avoid harsh chemicals and high-pressure washing."
    },
    {
      question: "Can I repair damaged areas?",
      answer: "Yes, damaged areas can usually be repaired. Contact us for assessment and repair options. It's best to address issues promptly to prevent further damage."
    },
    {
      question: "Will the surface fade over time?",
      answer: "Resin bound surfacing is UV stable and designed to maintain its color. Some slight fading may occur over many years, but the surface remains attractive throughout its lifespan."
    },
    {
      question: "Is resin bound surfacing suitable for heavy vehicles?",
      answer: "Yes, when properly installed with appropriate base preparation, resin bound surfacing can support heavy vehicles including cars, vans, and light commercial vehicles."
    }
  ],
  cost: [
    {
      question: "How much does resin bound surfacing cost?",
      answer: "Costs vary depending on size, complexity, and chosen materials. Prices typically range from £40-80 per square metre. Contact us for a detailed quote based on your specific requirements."
    },
    {
      question: "Do you offer financing options?",
      answer: "We can discuss various payment options and may be able to arrange flexible payment plans for larger projects. Please ask during your consultation."
    },
    {
      question: "What's included in the price?",
      answer: "Our quotes include all materials, labor, preparation work, and a comprehensive warranty. There are no hidden costs - everything is clearly outlined in your quote."
    },
    {
      question: "Are there any additional charges?",
      answer: "Additional charges may apply for complex access requirements, unusual site conditions, or specific design requests. All costs will be clearly explained in your detailed quote."
    }
  ]
};

const FAQPage: React.FC = () => {
  const [openItem, setOpenItem] = useState<string | null>(null);

  const toggleFAQ = (category: string, index: number) => {
    const key = `${category}-${index}`;
    setOpenItem(prev => prev === key ? null : key);
  };

  const categories = [
    { key: 'general', title: 'General Questions', icon: HelpCircle },
    { key: 'installation', title: 'Installation & Process', icon: Phone },
    { key: 'maintenance', title: 'Maintenance & Care', icon: Mail },
    { key: 'cost', title: 'Pricing & Costs', icon: MessageCircle }
  ];

  return (
    <>
      <Helmet>
        <title>Frequently Asked Questions - Resin Surfaces London</title>
        <meta name="description" content="Find answers to common questions about resin bound surfacing, installation, maintenance, and pricing. Expert advice from London's leading resin surfacing specialists." />
        <meta name="keywords" content="resin bound surfacing FAQ, resin driveway questions, resin surfacing maintenance, resin bound installation London" />
        <link rel="canonical" href="https://resinsurfaceslondon.co.uk/faq/" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://resinsurfaceslondon.co.uk/faq/" />
        <meta property="og:title" content="Frequently Asked Questions - Resin Surfaces London" />
        <meta property="og:description" content="Find answers to common questions about resin bound surfacing, installation, maintenance, and pricing." />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://resinsurfaceslondon.co.uk/faq/" />
        <meta property="twitter:title" content="Frequently Asked Questions - Resin Surfaces London" />
        <meta property="twitter:description" content="Find answers to common questions about resin bound surfacing, installation, maintenance, and pricing." />
      </Helmet>

      <HeroSectionComponent
        title="Frequently Asked Questions"
        subtitle="Find answers to common questions about resin bound surfacing, installation, maintenance, and pricing. Expert advice from London's leading resin surfacing specialists."
        badge="Expert Answers • Trusted Advice • 10+ Years Experience"
        backgroundImage="/images/resin-surfaces-bound-work1.jpeg"
        primaryButtonText="Get Free Quote"
        primaryButtonLink="/contact/"
        secondaryButtonText="Call Now"
        secondaryButtonLink="tel:+447535935779"
      />

      <FAQContainer>
        <Container>
          <Header>
            <Title>Frequently Asked Questions</Title>
            <Subtitle>
              Find answers to the most common questions about resin bound surfacing, installation, maintenance, and pricing.
            </Subtitle>
          </Header>

          <FAQGrid>
            {categories.map((category) => (
              <FAQCategory key={category.key}>
                <CategoryHeader>
                  <h2>
                    <category.icon size={24} />
                    {category.title}
                  </h2>
                </CategoryHeader>
                
                {faqData[category.key as keyof typeof faqData].map((item: FAQItemData, index: number) => {
                  const key = `${category.key}-${index}`;
                  const isOpen = openItem === key;
                  
                  return (
                    <FAQItem key={index}>
                      <FAQQuestion
                        isOpen={isOpen}
                        onClick={() => toggleFAQ(category.key, index)}
                      >
                        <h3>{item.question}</h3>
                        <ChevronDown size={20} />
                      </FAQQuestion>
                      <FAQAnswer isOpen={isOpen}>
                        <FAQAnswerContent>
                          <p>{item.answer}</p>
                        </FAQAnswerContent>
                      </FAQAnswer>
                    </FAQItem>
                  );
                })}
              </FAQCategory>
            ))}
          </FAQGrid>

          <ContactSection>
            <h2>Still Have Questions?</h2>
            <p>
              Can't find the answer you're looking for? Our expert team is here to help. 
              Get in touch for personalized advice and a free quote.
            </p>
            <ContactButtons>
              <WhatsAppButton href="https://wa.me/447535935779?text=Hi!%20I%20have%20a%20question%20about%20resin%20bound%20surfacing.%20Could%20you%20help%20me?">
                <MessageCircle size={20} />
                WhatsApp Chat
              </WhatsAppButton>
              <PhoneButton href="tel:+447535935779">
                <Phone size={20} />
                Call Now
              </PhoneButton>
              <EmailButton href="mailto:info@resinsurfaceslondon.co.uk">
                <Mail size={20} />
                Email Us
              </EmailButton>
            </ContactButtons>
          </ContactSection>
        </Container>
      </FAQContainer>
    </>
  );
};

export default FAQPage;
